import {ProductEntryLink} from './product-entry';
import {useEffect, useState, type FormEvent} from 'react';
import {TEAM_ROLES} from '../src/team';
import {ACCOUNT_STORAGE_LABEL} from '../src/storage-policy';
import {APP_BASE, Link} from './router';
import {ApiError, errorDetails, useHermes} from './lib/store';
import {SalesInquiryProblemSchema, SalesInquirySchema} from '../src/sales-inquiry';
import type {ErrorDetails} from './lib/types';
import {MarketingActions, MarketingClosing, MarketingPageIntro} from './marketing';
import {ErrorNotice, Loading} from './components';
import {desktopPlatformLabel, useDesktopRelease} from './lib/desktop-release';
import './desktop-downloads.css';
import './pricing.css';
import {SubscriptionPlansPage} from './billing';
import {PAYMENT_METHODS} from '../src/payment-method-catalog';
import {PaymentMethodList} from './payment-method-content';

export function DeveloperPage() {
  return <div className="marketing"><header className="marketing-page-intro"><h1>API coming soon.</h1></header></div>;
}

export function CompanyPage() {
  return <div className="marketing"><MarketingPageIntro label="Company" title="Make room for the work that matters.">EightBots gives one person a team they can direct.</MarketingPageIntro>
    <section className="marketing-page-section"><h2>Built around a working team.</h2><p>Useful work needs different skills. EightBots brings research, code, planning, design, writing, analysis, and operations together under Head of Eight.</p><p>You can see what each agent does, check its usage, and talk to it directly.</p></section>
    <section className="marketing-page-section"><h2>You stay involved.</h2><p>Sign in and give the team a goal. Review the work and tell it what to change.</p><p className="marketing-page-note">EightBots is in Development. This site describes the current product. It does not offer service guarantees.</p><Link className="marketing-text-link" href={`${APP_BASE}/contact-sales`}>Contact the team</Link></section><MarketingClosing/></div>;
}

export function PricingPage() {
  const {state, actions} = useHermes();
  useEffect(() => {void actions.loadBillingPlans().catch(() => undefined);}, [actions]);
  const subscriptionsAvailable = state.billingPlans.data?.configuration.checkoutEnabled === true;
  return <div className="marketing pricing-page">
    <MarketingPageIntro label="Pricing" title="See what your work costs.">Follow your team’s token use and estimated cost as you work.</MarketingPageIntro>
    {state.session.data && <SubscriptionPlansPage embedded/>}
    <section className="pricing-access" aria-labelledby="pricing-access-title">
      <div className="pricing-access-copy">
        <p className="marketing-label">Available now</p>
        <h2 id="pricing-access-title">Choose a plan for your team.</h2>
        <p>Create an account with email or Google, or sign in. Then choose a subscription. Your team and private workspace are prepared after payment is confirmed.</p>
        <MarketingActions guide={false}/>
        <p className="pricing-access-note">EightBots is in Development. {subscriptionsAvailable ? 'Subscription plans are available.' : 'Subscription availability is shown on the plans page.'}</p>
        {state.session.data && <Link className="marketing-text-link" href={`${APP_BASE}/subscribe`}>View subscription plans</Link>}
      </div>
      <dl className="pricing-included">
        <div><dt>Eight agents</dt><dd>Research, write, build, and plan with one team.</dd></div>
        <div><dt>Private conversations</dt><dd>Talk to the team or to one agent.</dd></div>
        <div><dt>{ACCOUNT_STORAGE_LABEL} of space</dt><dd>Keep your conversations and files together.</dd></div>
        <div><dt>Clear usage</dt><dd>See input tokens, output tokens, and estimated cost.</dd></div>
      </dl>
    </section>
    <div className="pricing-payment-groups">
      <section aria-labelledby="crypto-payments">
        <div className="pricing-payment-heading"><h2 id="crypto-payments">Crypto payments</h2><Link href={`${APP_BASE}/payment-methods/crypto`}>Explore every asset and network</Link></div>
        <PaymentMethodList methods={PAYMENT_METHODS.filter(method=>method.kind==='crypto')}/>
      </section>
      <section aria-labelledby="fiat-payments">
        <div className="pricing-payment-heading"><h2 id="fiat-payments">Fiat payments</h2><p>{subscriptionsAvailable ? 'Available payment methods are shown at checkout.' : 'Coming soon'}</p></div>
        <PaymentMethodList methods={PAYMENT_METHODS.filter(method=>method.kind!=='crypto')}/>
      </section>
    </div>
    <section className="pricing-model-use" aria-labelledby="pricing-model-title">
      <div><h2 id="pricing-model-title">Track your model use.</h2><p>Your plan includes credits for model use.</p></div>
      <div><p>Recorded use reduces your available credits.</p><p>Your chat’s estimated cost uses recorded token counts and the saved model rates.</p></div>
    </section>
  </div>;
}

export function NewsPage() {
  return <div className="marketing"><MarketingPageIntro label="News" title="Meet EightBots.">A team of eight AI agents that works for you.</MarketingPageIntro>
    <article className="marketing-news-entry"><h2>Give your team a goal.</h2><p>EightBots brings {TEAM_ROLES.map(role => role.name).join(', ')} into one workspace. Head of Eight coordinates the team. You can select any agent to follow its work and give instructions.</p>
      <p>Sign in with your email and password. Read your agents’ current activity and check their model use.</p><p className="marketing-page-note">The current version is in Development. Your subscription includes credits for model use, with model access managed by EightBots.</p><ProductEntryLink className="marketing-text-link" workspaceHref={`${APP_BASE}/workspace`}/></article><MarketingClosing/></div>;
}

interface SalesReceipt {id: string; receivedAt: string}
export function ContactSalesPage() {
  const [state, setState] = useState<{status: 'idle' | 'sending' | 'received' | 'error'; receipt: SalesReceipt | null; error: ErrorDetails | null}>({status: 'idle', receipt: null, error: null});
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    const submission = SalesInquirySchema.safeParse({name: values.get('name'), email: values.get('email'), message: values.get('message')});
    if (!submission.success) {
      setState({status: 'error', receipt: null, error: {name:'ValidationError',message:submission.error.issues[0]!.message,cause:errorDetails(submission.error)}});
      return;
    }
    setState({status: 'sending', receipt: null, error: null});
    try {
      const response = await fetch(`${APP_BASE}/api/sales-inquiries`, {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify(submission.data)});
      const text = await response.text();
      let body: unknown;
      try {body = JSON.parse(text);}
      catch (cause) {throw new ApiError({name: 'InvalidApiResponse', message: `HTTP ${response.status}: ${text}`, status: response.status, cause: errorDetails(cause)});}
      if (!response.ok) {
        const details = typeof body === 'object' && body !== null && 'error' in body ? body.error : null;
        const problem = SalesInquiryProblemSchema.safeParse(details);
        if (problem.success) {
          setState({status: 'error', receipt: null, error: {name:'SalesInquiryError',...problem.data,status:response.status,cause:details}});
          return;
        }
        throw new ApiError({name: 'HttpError', message: `HTTP ${response.status}: ${text}`, status: response.status});
      }
      if (typeof body !== 'object' || body === null || !('id' in body) || typeof body.id !== 'string' || !('receivedAt' in body) || typeof body.receivedAt !== 'string') {
        throw new ApiError({name: 'InvalidSalesReceipt', message: `The response is missing the saved request reference: ${text}`, status: response.status});
      }
      setState({status: 'received', receipt: {id: body.id, receivedAt: body.receivedAt}, error: null});
    } catch (error) {
      console.error(error);
      setState({status: 'error', receipt: null, error: errorDetails(error)});
    }
  }
  return <div className="marketing"><MarketingPageIntro label="Contact sales" title="Tell us what your team needs.">Share the work you want EightBots to help with.</MarketingPageIntro>
    <form className="marketing-form" onSubmit={event => {void submit(event);}}>
      <Loading active={state.status === 'sending'}/>
      <label>Name<input name="name" autoComplete="name" required disabled={state.status === 'sending'}/></label>
      <label>Work email<input name="email" type="email" autoComplete="email" required disabled={state.status === 'sending'}/></label>
      <label>What do you want to do?<textarea name="message" required disabled={state.status === 'sending'}/></label>
      <p className="marketing-form-note">Your request and contact details will be saved for the EightBots team. Do not include passwords, keys, or wallet recovery phrases.</p>
      <button className="marketing-button" type="submit" disabled={state.status === 'sending'}>Send request</button>
      {state.receipt && <div className="marketing-form-success" role="status"><strong>Your request is saved.</strong><br/>Reference: <code>{state.receipt.id}</code></div>}
      <ErrorNotice title="There is a problem with your request" error={state.error}/>
    </form>
  </div>;
}

export function DownloadPage() {
  const state = useDesktopRelease();
  return <div className="marketing"><MarketingPageIntro label="Download" title="Use EightBots on your computer.">Continue your chats and work with the same account.</MarketingPageIntro>
    <Loading active={state.status === 'loading'}/>
    {state.status === 'loading' && <p role="status">Checking desktop downloads…</p>}
    {state.status === 'unpublished' && <p role="status">Desktop downloads are not available yet. You can use EightBots in your browser.</p>}
    {state.status === 'error' && <ErrorNotice title="Downloads could not be loaded." error={state.error}/>}
    {state.status === 'available' && <section className="marketing-page-section" aria-labelledby="desktop-download-title">
      <h2 id="desktop-download-title">Choose your computer.</h2>
      <ul className="desktop-download-links">{state.release.downloads.map(download => <li key={download.platform}>
        <a className="marketing-button" href={download.url} download>Download for {desktopPlatformLabel(download.platform)}</a>
      </li>)}</ul>
      <p className="marketing-page-note">Version {state.release.version}. Windows installers are signed. Mac installers are signed and notarized.</p>
    </section>}
    <div className="marketing-actions"><Link className="marketing-text-link" href={`${APP_BASE}/`}>Back to EightBots</Link></div></div>;
}
